// Which blocks of the Notes screen are collapsed, and how that is remembered.
//
// PER BROWSER, exactly as the theme and the wording are, and for the same reason
// rather than by omission: it is a view state, not a fact about the account.
// Collapsing the pinned area on a phone where the screen is small should not
// fold it up on the desktop the same person opens next, and there is no
// `preferences` write behind this - what is below is the whole of the storage.
//
// `localStorage` ONLY, AND NOT `shell/preference.ts`, WHICH IS THE DECISION
// HERE. That module writes both mirrors, and the cookie half is what makes it
// wrong for this one value: it is BOUNDED preferences only (it says so), and
// this value is a list of section uuids on a table that is deliberately
// uncapped. Mirrored, a person with many folded sections would send multiple
// kilobytes of cookie with every API call and every asset fetch, for ever, and
// past roughly a hundred keys the browser would reject the cookie outright.
// What the mirror buys is survival of Safari's seven-day eviction of
// script-written storage - which matters for the THEME, because the app comes
// back looking wrong, and is a non-event here: a section re-expanding after a
// week away costs nothing and is one press to undo. Mirroring only SOME of the
// keys was rejected: it would mean deciding which sections are worth
// remembering, which is arbitrary and unexplainable to the person it happens
// to, and it would be a cap - and caps in this product are the owner's, never
// a worker's.
//
// THE VALUE IS A LIST OF KEYS THAT ARE COLLAPSED, so the default - nothing
// stored - is everything expanded, which is the right default in both
// directions: a browser with no storage at all draws the screen open rather than
// shut, and a group made after the choice was last written starts open.
//
// Space-separated rather than JSON. Every key is a uuid or one of two literals,
// none of which can contain a space, so the format cannot be malformed by
// anything this module writes - and a parse that cannot fail is one fewer catch
// around a preference that must never cost a blank screen.
//
// NEITHER FUNCTION MAY THROW OUT OF HERE, the same discipline
// `shell/preference.ts` keeps and for the same reason: storage can be disabled
// outright, in which case touching it raises rather than answering, and this is
// read while the screen renders. A cosmetic choice may not cost a blank screen,
// so a failure is nothing folded rather than nothing drawn.

const FOLDED_KEY = 'pn_notes_folded';

/** No group folded, as one frozen value, so "nothing collapsed" is one identity. */
const NONE: ReadonlySet<string> = new Set<string>();

export function readFolded(): ReadonlySet<string> {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(FOLDED_KEY);
  } catch {
    // Disabled, or absent. Nothing folded is the safe answer: it draws the
    // screen open, which is the state every control on it can change.
    return NONE;
  }
  if (stored === null || stored.trim() === '') return NONE;
  return new Set(stored.split(' ').filter((key) => key !== ''));
}

/**
 * Write the set down, keeping only the keys that still exist.
 *
 * PRUNED ON EVERY WRITE, against the groups on screen at the time. Without it
 * the value grows by one uuid for every group that is ever collapsed and then
 * deleted, and it is never read again - a preference that only ever gets longer
 * is a preference that eventually fails to store. `known` is the keys the screen
 * is drawing, which is exactly the set that can still be toggled.
 *
 * The key being written is added to `known` by the caller's own set, so a group
 * collapsed in the same act is never pruned by the act that collapsed it.
 */
export function writeFolded(folded: ReadonlySet<string>, known: ReadonlySet<string>): void {
  const kept = [...folded].filter((key) => known.has(key));
  try {
    window.localStorage.setItem(FOLDED_KEY, kept.join(' '));
  } catch {
    // Full, disabled, or private browsing. The choice holds for this page and
    // is forgotten on the next one, which is what "remembered where it can be"
    // looks like - and is never worth an error reaching the screen.
  }
}

/** The set with one key flipped. A new value each time, so React sees the change. */
export function toggleFolded(folded: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(folded);
  if (!next.delete(key)) next.add(key);
  return next;
}
